import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FadeIn from '../components/FadeIn';

const minimums = [
  { day: 'Weekdays', amount: '$16,000' },
  { day: 'Fridays', amount: '$23,000' },
  { day: 'Saturdays & holidays', amount: '$31,000' },
  { day: 'Sundays', amount: '$20,000' }
];

const factors = [
  {
    title: 'Date & Season',
    text: 'Minimums vary by day of the week and season. Saturdays in peak seasons may have higher event minimums than Fridays, Sundays, or off-peak dates.'
  },
  {
    title: 'Guest Count',
    text: 'Your guest count affects food and beverage selections, room setup, rentals, and the overall event plan. The ballroom accommodates up to 250 guests depending on setup.'
  },
  {
    title: 'Food & Beverage Selections',
    text: 'Menu, dining style, and bar selections are discussed with our team and reflected in your custom proposal.'
  },
  {
    title: 'Event Details',
    text: 'Event timing, rentals, enhancements, and other details can affect your proposal. Ask the team to clarify each applicable item.'
  }
];

function PublishedMinimums() {
  return (
    <section className="py-16 md:py-20 bg-white px-6">
      <div className="max-w-5xl mx-auto">
        <FadeIn>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-brand-olive uppercase tracking-[0.3em] text-[10px] font-bold mb-4 block">
              Planning Your Celebration
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-5 font-light">
              Published Event Minimums
            </h2>
            <p className="font-sans text-brand-charcoal/75 leading-relaxed font-light">
              Published minimum spend requirements, excluding alcohol. These are not a venue rental fee or a complete wedding price.
            </p>
          </div>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="Published event minimums by day">
          {minimums.map((item) => (
            <div key={item.day} className="flex items-center justify-between gap-4 border border-brand-stone/40 bg-brand-vintage px-5 py-5 md:px-7">
              <span className="font-sans text-sm md:text-base text-brand-charcoal">{item.day}</span>
              <span className="font-serif text-xl md:text-2xl text-brand-olive whitespace-nowrap">{item.amount}</span>
            </div>
          ))}
        </div>
        <div className="mt-8 max-w-3xl mx-auto space-y-4 text-center">
          <p className="font-sans text-sm md:text-base text-brand-charcoal/75 leading-relaxed">
            Your final pricing depends on your date, guest count, menu, bar service, rentals, enhancements, administrative fee, tax, and event details. Pricing and availability are subject to confirmation by our team.
          </p>
          <p className="font-sans text-sm text-brand-charcoal/70 leading-relaxed">
            A smaller guest count does not automatically reduce the applicable minimum. Ceremony-only options have separate planning details and should not be compared with full-service event minimums.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <a
              href="https://guide.paduaweddings.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center border border-brand-charcoal px-6 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-brand-charcoal hover:bg-brand-aged transition-colors"
            >
              Explore the Wedding Planning Guide
            </a>
            <Link
              to="/weddings/faq"
              className="inline-flex justify-center px-6 py-3 text-[10px] uppercase tracking-[0.2em] font-bold text-brand-olive border border-brand-olive hover:bg-brand-olive hover:text-white transition-colors"
            >
              Read Wedding FAQs
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProposalDetails() {
  return (
    <section className="py-16 md:py-20 bg-brand-aged px-6">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <FadeIn>
          <span className="text-brand-bronze uppercase tracking-[0.3em] text-[10px] font-bold mb-4 block">
            Clear, Itemized Details
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-6 font-light">
            What Your Proposal Clarifies
          </h2>
          <p className="font-sans text-brand-charcoal/75 leading-relaxed font-light">
            Ask for an itemized estimate so you can review the arrangements and charges that apply to your celebration before making decisions.
          </p>
          <ul className="mt-6 space-y-3 font-sans text-sm text-brand-charcoal/80">
            <li>Menu selections and dining style</li>
            <li>Guest count, room setup, and event layout</li>
            <li>Bar selections and service</li>
            <li>Rentals and enhancements</li>
            <li>Applicable administrative fees and sales tax</li>
          </ul>
        </FadeIn>
        <FadeIn delay={0.1} className="bg-white p-7 md:p-9 border border-brand-stone/30">
          <h3 className="font-serif text-2xl text-brand-charcoal mb-4">Fees & tax</h3>
          <p className="font-sans text-sm text-brand-charcoal/75 leading-relaxed mb-4">
            Full-service events have a 22% administrative fee and applicable sales tax. Outdoor dining has a 25% administrative fee, with applicable sales tax.
          </p>
          <p className="font-sans text-sm text-brand-charcoal/75 leading-relaxed">
            Administrative fees are not gratuities. Your proposal should clarify the fees and tax applicable to your event. No all-in total is implied by the published minimums.
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

function ExistingPricingFactors() {
  return (
    <>
      <section className="py-20 px-6 max-w-4xl mx-auto text-center">
        <FadeIn>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-8 font-light">
            A Tailored Approach to Your Celebration
          </h2>
          <p className="font-sans text-brand-charcoal/80 text-lg leading-relaxed font-light">
            Your wedding proposal is built around the event you are planning. Review the factors below with our team to understand the choices, arrangements, and costs that apply to your date and guest count.
          </p>
        </FadeIn>
      </section>
      <section className="py-16 bg-white px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {factors.map((factor, index) => (
            <FadeIn key={factor.title} delay={index * 0.08} className="bg-brand-vintage p-8 md:p-10 border border-brand-stone/20">
              <h3 className="font-serif text-2xl text-brand-charcoal mb-4">{factor.title}</h3>
              <p className="font-sans text-brand-charcoal/70 font-light leading-relaxed">{factor.text}</p>
            </FadeIn>
          ))}
        </div>
      </section>
    </>
  );
}

export default function Investment() {
  return (
    <main className="bg-brand-vintage pb-0">
      <SeoMeta
        title="Padua Wedding Cost & Event Minimums | Padua Weddings"
        description="Review Padua Hills Theatre wedding cost guidance, published event minimums, administrative fees, and the details that shape a custom Padua wedding pricing proposal."
      />
      <PageHero
        title="Investment & Minimums"
        subtitle="Understand published event minimums and the details that shape a custom proposal."
        image="https://ik.imagekit.io/aeu02xvjgn/2020-06-10-Padua-Menu-0322.jpg"
      />
      <PublishedMinimums />
      <ProposalDetails />
      <ExistingPricingFactors />
      <CTASection
        title="Receive a Custom Estimate"
        desc="Share your date, guest count, and celebration plans so our team can prepare an itemized proposal."
        primaryText="Request an Estimate"
        primaryLink="/#contact"
        secondaryText="Book a Private Tour"
        secondaryLink="/weddings/private-tour"
      />
    </main>
  );
}